import { Entity } from "./Entity.js";
import { GRAVITY } from "../constants.js";

export class Npc extends Entity {
  constructor(scene, x, y, typeDef, options = {}) {
    super(scene, x, y - typeDef.h, typeDef.w, typeDef.h, 1);
    this.setHurtboxProfile("default");
    this.typeDef = typeDef;
    this.prefix = typeDef.prefix;
    this.spawnX = x;
    this.spawnY = y - typeDef.h;
    this.sprite = null;
    this.onGround = false;

    this.speed = Number.isFinite(options.speed) ? options.speed : (typeDef.speed || 0.9);
    this.patrolDistance = Number.isFinite(options.patrolDistance) ? options.patrolDistance : (typeDef.patrolDistance || 180);
    this.aiState = "patrol";
    this.idleTimer = 0;
    this.idleDuration = 300 + Math.random() * 400;
    this.patrolDir = Math.random() < 0.5 ? 1 : -1;
    this.turnCooldown = 0;
  }

  animKey(suffix) {
    return this.prefix + "_" + suffix;
  }

  create() {
    this.sprite = this.scene.add.sprite(0, 0, this.animKey("idle"))
      .setOrigin(this.typeDef.originX, this.typeDef.originY)
      .setDepth(1000);
    this.sprite.play(this.animKey("idle"));
    this.syncSprite();
  }

  syncSprite() {
    const targetCenterX = this.x + this.w / 2;
    this.sprite.x = targetCenterX;
    this.sprite.y = this.y + this.h;
    this.sprite.setFlipX(this.facing === -1);

    // Keep visual center pinned when animation frames are asymmetric.
    const b = this.sprite.getBounds();
    this.sprite.x += targetCenterX - b.centerX;
    this.sprite.setDepth(1000 + this.sprite.y);
  }

  hasGroundAhead(dir) {
    const checkX = dir > 0 ? this.x + this.w + 6 : this.x - 6;
    const feetY = this.y + this.h;
    const result = this.scene.findGroundY(checkX, feetY - 2, feetY + 56, null);
    return result !== null;
  }

  takeDamage() {
    // NPCs are non-combatants in this phase.
    return false;
  }

  updateAI(dt) {
    this.turnCooldown = Math.max(0, this.turnCooldown - dt);

    switch (this.aiState) {
      case "idle":
        this.vx = 0;
        this.idleTimer += dt;
        if (this.idleTimer >= this.idleDuration) {
          this.aiState = "patrol";
          this.idleTimer = 0;
        }
        break;
      case "patrol": {
        this.vx = this.speed * this.patrolDir;
        this.facing = this.patrolDir;
        const distFromSpawn = this.x + this.w / 2 - this.spawnX;
        const hitPatrolEdge = Math.abs(distFromSpawn) > this.patrolDistance;
        const nearLedge = this.onGround && !this.hasGroundAhead(this.patrolDir);
        if ((hitPatrolEdge || nearLedge) && this.turnCooldown <= 0) {
          this.patrolDir *= -1;
          this.facing = this.patrolDir;
          this.vx = this.speed * this.patrolDir;
          this.turnCooldown = 220;
        }
        break;
      }
      default:
        this.aiState = "idle";
        this.vx = 0;
        break;
    }
  }

  resolveAnimState() {
    const moving = Math.abs(this.vx) > 0.1;
    const next = moving ? this.animKey("walking") : this.animKey("idle");
    if (this.sprite.anims.currentAnim?.key !== next) {
      this.sprite.play(next);
    }
  }

  update(dt) {
    super.update(dt);
    const dtScale = this.scene.frameScale || 1;

    if (!this.onGround) {
      this.vy += GRAVITY * dtScale;
    }

    this.updateAI(dt);
    this.resolveAnimState();
    this.syncSprite();
  }

  destroy() {
    if (this.sprite) {
      this.sprite.destroy();
      this.sprite = null;
    }
  }
}
